import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { FLOW } from '../content/siteContent';
import { useReducedMotion, useIsDesktop } from '../hooks/useMotionPrefs';

const PATH_PROPS = {
  pathLength: 1,
  strokeDasharray: 1,
  strokeDashoffset: 1,
};

export default function EnergyFlow() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (reduced) {
      // Show the completed system statically
      ref.current.querySelectorAll('path').forEach((p) => {
        p.style.strokeDashoffset = 0;
      });
      ref.current.querySelectorAll('.flow-node, .flow-label').forEach((n) => {
        n.style.opacity = 1;
      });
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 65%',
          toggleActions: 'play none none none',
        },
      });
      tl.timeScale(0.4);
      tl.fromTo('.flow-heading', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.12 }, 0)
        // 0% → 40% : one point, then the trunk line draws itself
        .fromTo('.flow-origin', { scale: 0, opacity: 0, transformOrigin: 'center' }, { scale: 1, opacity: 1, duration: 0.06 }, 0.05)
        .to('.flow-trunk', { strokeDashoffset: 0, ease: 'none', duration: 0.34 }, 0.1)
        .fromTo('.flow-hub', { scale: 0, opacity: 0, transformOrigin: 'center' }, { scale: 1, opacity: 1, duration: 0.06 }, 0.42)
        // 40% → 80% : branches expand
        .to('.flow-branch-1', { strokeDashoffset: 0, ease: 'none', duration: 0.2 }, 0.46)
        .to('.flow-branch-2', { strokeDashoffset: 0, ease: 'none', duration: 0.2 }, 0.54)
        .to('.flow-branch-3', { strokeDashoffset: 0, ease: 'none', duration: 0.2 }, 0.62)
        // 80% → 100% : nodes appear — the ecosystem forms
        .fromTo('.flow-node', { scale: 0, opacity: 0, transformOrigin: 'center' }, { scale: 1, opacity: 1, stagger: 0.06, duration: 0.08 }, 0.78)
        .fromTo('.flow-label', { opacity: 0, y: 12 }, { opacity: 1, y: 0, stagger: 0.05, duration: 0.08 }, 0.86);
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="flow"
      data-testid="energy-flow-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <div className="absolute left-5 top-24 z-10 md:left-12 md:top-28">
        <SectionTag number="04" label="Energy Flow" />
        <h2 className="flow-heading mt-6 font-display text-2xl font-semibold tracking-tight text-white md:text-4xl">
          {FLOW.heading}
        </h2>
      </div>

      <svg
        viewBox={isDesktop ? '0 0 1440 700' : '260 120 1120 500'}
        preserveAspectRatio={isDesktop ? 'xMidYMid meet' : 'xMidYMid meet'}
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        {/* trunk */}
        <path
          className="flow-trunk energy-glow"
          d="M -20 380 C 220 370 420 390 660 380"
          fill="none"
          stroke="#47a0d9"
          strokeWidth="2"
          {...PATH_PROPS}
        />
        {/* branches */}
        <path
          className="flow-branch-1 energy-glow"
          d="M 660 380 C 800 380 850 190 1010 190 C 1090 190 1130 190 1180 190"
          fill="none"
          stroke="#47a0d9"
          strokeWidth="1.6"
          {...PATH_PROPS}
        />
        <path
          className="flow-branch-2 energy-glow"
          d="M 660 380 C 830 385 1000 375 1180 380"
          fill="none"
          stroke="#47a0d9"
          strokeWidth="1.6"
          {...PATH_PROPS}
        />
        <path
          className="flow-branch-3 energy-glow"
          d="M 660 380 C 800 380 850 560 1010 560 C 1090 560 1130 560 1180 560"
          fill="none"
          stroke="#47a0d9"
          strokeWidth="1.6"
          {...PATH_PROPS}
        />

        <circle className="flow-origin" cx="40" cy="380" r="6" fill="#47a0d9" />
        <circle className="flow-hub" cx="660" cy="380" r="9" fill="#050505" stroke="#47a0d9" strokeWidth="2" />
        <circle className="flow-node node-dot" cx="1180" cy="190" r="7" fill="#47a0d9" />
        <circle className="flow-node node-dot" cx="1180" cy="380" r="7" fill="#47a0d9" />
        <circle className="flow-node node-dot" cx="1180" cy="560" r="7" fill="#47a0d9" />

        <text className="flow-label fill-white/60" x="1210" y="195" fontSize="15" letterSpacing="3" style={{ textTransform: 'uppercase' }}>
          {FLOW.branchLabels[0]}
        </text>
        <text className="flow-label fill-white/60" x="1210" y="385" fontSize="15" letterSpacing="3">
          {FLOW.branchLabels[1]}
        </text>
        <text className="flow-label fill-white/60" x="1210" y="565" fontSize="15" letterSpacing="3">
          {FLOW.branchLabels[2]}
        </text>
      </svg>

      <p className="absolute bottom-10 left-5 max-w-xs text-[11px] uppercase tracking-[0.22em] text-white/40 md:left-12">
        One energy system — watch the network draw itself
      </p>
    </section>
  );
}
